import {LEFT_NAV} from "./nav/DefaultNav";
import {ADMIN_NAV} from "./nav/AdminNav";
import {BUYER_DISPATCHER_NAV, BUYER_OWNER_NAV} from "./nav/BuyerNav";
import {
  makeGateManagerNav,
  makeSupplierOwnerGMSLiteNav,
  makeSupplierOwnerGMSProNav,
  makeSupplierMarketPlaceOnlyNav,
  MARKETPLACE_ONLY_GATE_USER_NAV,
  SUPPLIER_GATE_CLERK_NAV
} from "./nav/SupplierNav";
import {SubscriptionType} from "../components/constants/securspace-constants";
import {
  hasOwnerAccess,
  hasGateManagerAccess,
  hasGateClerkAccess,
  isAdminAccount,
  isBuyerAccount,
  isSupplierAccount
} from "../util/AccountUtils";
import {Account, NavItem} from "../interfaces";

/**
 * Type for user objects with the minimum properties needed for navigation
 */
type UserForNav = Pick<Account, 'type' | 'userType' | 'subscriptionType'>;

/**
 * Get navigation options based on user account type, user type, and subscription
 * @param user - Account object or partial account with required navigation properties
 * @returns Array of navigation items for the user
 */
export const getUserBasedNavOptions = (user: UserForNav | null | undefined): NavItem[] => {
  if (!user) {
    return LEFT_NAV;
  }

  let navOptions: NavItem[];

  if (isAdminAccount(user)) {
    navOptions = ADMIN_NAV;
  } else if (isBuyerAccount(user)) {
    navOptions = getBuyerNavOptions(user);
  } else if (isSupplierAccount(user)) {
    navOptions = getSupplierNavOptions(user);
  } else {
    navOptions = LEFT_NAV;
  }

  return navOptions;
};

const getSupplierNavOptions = (user: UserForNav): NavItem[] => {
  const {subscriptionType} = user;

  let navOptions: NavItem[];

  switch (subscriptionType) {
    case SubscriptionType.GMS_LITE:
    case SubscriptionType.GMS_PRO:
      navOptions = getSupplierGmsSubscribersNavOptions(user);
      break;
    case SubscriptionType.MARKETPLACE_ONLY:
      navOptions = getSupplierMarketplaceOnlyNavOptions(user);
      break;
    default:
      navOptions = LEFT_NAV;
  }

  return navOptions;
};

const getSupplierGmsSubscribersNavOptions = (user: UserForNav): NavItem[] => {
  let navOptions: NavItem[];

  if (hasOwnerAccess(user)) {
    navOptions = getSupplierOwnerNavOptions(user, user.subscriptionType);
  } else if (hasGateManagerAccess(user)) {
    navOptions = makeGateManagerNav();
  } else if (hasGateClerkAccess(user)) {
    navOptions = SUPPLIER_GATE_CLERK_NAV;
  } else {
    navOptions = LEFT_NAV;
  }

  return navOptions;
};

const getSupplierMarketplaceOnlyNavOptions = (user: UserForNav): NavItem[] => {
  let navOptions: NavItem[];

  if (hasOwnerAccess(user)) {
    navOptions = getSupplierOwnerNavOptions(user, SubscriptionType.MARKETPLACE_ONLY);
  } else if (hasGateClerkAccess(user)) {
    navOptions = MARKETPLACE_ONLY_GATE_USER_NAV;
  } else {
    navOptions = LEFT_NAV;
  }

  return navOptions;
};

const getSupplierOwnerNavOptions = (user: UserForNav, subscriptionType: string): NavItem[] => {
  let navOptions: NavItem[];

  switch (subscriptionType) {
    case SubscriptionType.MARKETPLACE_ONLY:
      navOptions = makeSupplierMarketPlaceOnlyNav(user);
      break;
    case SubscriptionType.GMS_LITE:
      navOptions = makeSupplierOwnerGMSLiteNav(user);
      break;
    case SubscriptionType.GMS_PRO:
      navOptions = makeSupplierOwnerGMSProNav(user);
      break;
    default:
      navOptions = LEFT_NAV;
  }

  return navOptions;
};

const getBuyerNavOptions = (user: UserForNav): NavItem[] => {
  if (hasOwnerAccess(user)) {
    return BUYER_OWNER_NAV;
  } else {
    return BUYER_DISPATCHER_NAV;
  }
};

/* * * * * * * * **
CMM (31 Jul 2025): this function is not presently used in the codebase, but it is useful for filtering
                   navigation options based on feature flags. Leaving the code in place for potential future use.


export const filterNavOptions = (navOptions: NavItem[], flags: { [path: string]: boolean }): NavItem[] => {
    const filteredNavOptions: NavItem[] = [];
    navOptions.forEach((navOption) => {
        if (!navOption || !navOption.submenu || navOption.submenu.length === 0) {
            filteredNavOptions.push(navOption);
        } else {
            const { submenu } = navOption;
            const newSubmenu = submenu.filter((subOption) => {
                return flags[subOption.path] !== undefined ? flags[subOption.path] : true;
            });
            filteredNavOptions.push({
                ...navOption,
                submenu: newSubmenu
            });
        }
    });
    return filteredNavOptions;
};
* * * * * * *  */
